import { getOrderBetween } from '@epam/uui-core';
import { ModalFnArgs } from 'components/modals';
import { useTaskGroupsCreateQuery, useTaskGroupsUpdateQuery } from 'hooks/api';
import { useModal } from 'hooks/ui';
import { useCallback } from 'react';
import { resetDraftTaskGroup, useTaskGroupsStore } from 'store/task-groups';

type PromptToUpdateParams = Pick<ModalFnArgs, 'onSuccess' | 'onAbort'>;

export const useLeaveTaskGroupsModal = () => {
  const modal = useModal();
  const { mutate: updateTaskGroup } = useTaskGroupsUpdateQuery();
  const { mutate: createTaskGroup } = useTaskGroupsCreateQuery();
  const {
    isControlsShown,
    isDraftTaskGroupValid,
    taskGroups,
    draftTaskGroup,
    selectedTaskGroup,
  } = useTaskGroupsStore((state) => state);

  const isEditingMode = !!selectedTaskGroup;

  const createUpdateTaskGroup = useCallback(() => {
    if (isEditingMode) {
      updateTaskGroup({
        id: selectedTaskGroup.id,
        taskGroup: {
          ...selectedTaskGroup,
          ...draftTaskGroup,
        },
      });
    } else {
      const lastTaskGroupOrder = taskGroups.at(-1)?.order ?? null;

      createTaskGroup({
        ...draftTaskGroup,
        order: getOrderBetween(lastTaskGroupOrder, null),
      });
    }
  }, [
    isEditingMode,
    selectedTaskGroup,
    draftTaskGroup,
    taskGroups,
    updateTaskGroup,
    createTaskGroup,
  ]);

  const promptToUpdate = useCallback(
    ({ onSuccess, onAbort }: PromptToUpdateParams) =>
      modal.save({
        isDisabled: !isDraftTaskGroupValid,
        highlights: ['save'],
        text: 'Do you want to save the changes? \n Otherwise the changes will be lost.',
        onSuccess: () => {
          createUpdateTaskGroup();
          resetDraftTaskGroup();
          onSuccess();
        },
        onAbort: () => {
          resetDraftTaskGroup();
          onAbort?.();
        },
      }),
    [modal, isDraftTaskGroupValid, createUpdateTaskGroup],
  );

  return useCallback(
    ({ onSuccess, onAbort }: PromptToUpdateParams) => {
      if (!isControlsShown) {
        promptToUpdate({ onSuccess, onAbort });
      } else {
        onSuccess();
      }
    },
    [isControlsShown, promptToUpdate],
  );
};
